import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  fetchDynamicFilterReport,
  fetchStates,
  fetchDistricts,
  fetchDistrictsByState,
  fetchTalukas,
  fetchTalukasByDistrict,
  fetchVillagesByTaluka,
  fetchProfessions,
  fetchCasts,
  fetchCastCategories,
  fetchEducation
} from '../utils/api';
import {
  INCOME_RANGE_OPTIONS,
  getIncomeRange,
  parseIncomeRange
} from '../utils/incomeRange';

const EMPTY_FILTERS = {
  gender: null,
  state: null,
  district: null,
  taluka: null,
  village: null,
  profession: null,
  castcategory: null,
  cast: null,
  education: null,
  incomeRange: null,
  disability: null
};

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];
const DISABILITY_OPTIONS = ['Yes', 'No'];

const CHART_COLORS = [
  '#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f',
  '#0288d1', '#558b2f', '#f9a825', '#6a1b9a', '#c62828'
];

const FILTER_LABELS = {
  gender: 'Gender',
  state: 'State',
  district: 'District',
  taluka: 'Taluka',
  village: 'Village',
  profession: 'Profession',
  castcategory: 'Caste Category',
  cast: 'Caste',
  education: 'Education',
  minIncome: 'Min Income',
  maxIncome: 'Max Income',
  incomeRange: 'Income Range',
  disability: 'Disability'
};

const getOptionId = (option, idKey) => {
  if (!option) return null;
  return option[idKey] ?? option.id ?? null;
};

const getOptionName = (option, nameKey) => {
  if (!option) return '';
  if (typeof option === 'string') return option;
  return option[nameKey] || option.name || '';
};

const buildGroupLabel = (groupValues = {}) => {
  const entries = Object.entries(groupValues);
  if (entries.length === 0) return 'All';
  return entries
    .map(([key, value]) => `${FILTER_LABELS[key] || key}: ${value}`)
    .join(' | ');
};

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportRows, setReportRows] = useState([]);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [chartType, setChartType] = useState('bar');

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [castCategories, setCastCategories] = useState([]);
  const [casts, setCasts] = useState([]);
  const [educationOptions, setEducationOptions] = useState([]);

  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [filteredTalukas, setFilteredTalukas] = useState([]);
  const [filteredVillages, setFilteredVillages] = useState([]);

  const [selectedFilters, setSelectedFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });

  useEffect(() => {
    let cancelled = false;
    const loadMasterData = async () => {
      try {
        const [
          statesResponse,
          districtsResponse,
          talukasResponse,
          professionsResponse,
          castCategoriesResponse,
          educationResponse,
          castsResponse
        ] = await Promise.all([
          fetchStates(),
          fetchDistricts(),
          fetchTalukas(),
          fetchProfessions(),
          fetchCastCategories(),
          fetchEducation(),
          fetchCasts()
        ]);
        if (cancelled) return;

        setStates(statesResponse.data || []);
        setDistricts(districtsResponse.data || []);
        setTalukas(talukasResponse.data || []);
        setProfessions(professionsResponse.data || []);
        setCastCategories(castCategoriesResponse.data || []);
        setEducationOptions(educationResponse.data || []);
        setCasts(castsResponse.data || []);
      } catch (err) {
        console.error('Error loading master data:', err);
      }
    };

    loadMasterData();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedStateId = getOptionId(selectedFilters.state, 'stateId');
  const selectedDistrictId = getOptionId(selectedFilters.district, 'districtId');
  const selectedTalukaId = getOptionId(selectedFilters.taluka, 'talukaId');

  useEffect(() => {
    let cancelled = false;
    const loadDistrictsForState = async () => {
      if (!selectedStateId) {
        setFilteredDistricts([]);
        return;
      }
      try {
        const response = await fetchDistrictsByState(selectedStateId);
        if (!cancelled) setFilteredDistricts(response.data || []);
      } catch (err) {
        console.error('Error loading districts:', err);
        if (!cancelled) setFilteredDistricts([]);
      }
    };
    loadDistrictsForState();
    return () => {
      cancelled = true;
    };
  }, [selectedStateId]);

  useEffect(() => {
    let cancelled = false;
    const loadTalukasForDistrict = async () => {
      if (!selectedDistrictId) {
        setFilteredTalukas([]);
        return;
      }
      try {
        const response = await fetchTalukasByDistrict(selectedDistrictId);
        if (!cancelled) setFilteredTalukas(response.data || []);
      } catch (err) {
        console.error('Error loading talukas:', err);
        if (!cancelled) setFilteredTalukas([]);
      }
    };
    loadTalukasForDistrict();
    return () => {
      cancelled = true;
    };
  }, [selectedDistrictId]);

  useEffect(() => {
    let cancelled = false;
    const loadVillagesForTaluka = async () => {
      if (!selectedTalukaId) {
        setFilteredVillages([]);
        return;
      }
      try {
        const response = await fetchVillagesByTaluka(selectedTalukaId);
        if (!cancelled) setFilteredVillages(response.data || []);
      } catch (err) {
        console.error('Error loading villages:', err);
        if (!cancelled) setFilteredVillages([]);
      }
    };
    loadVillagesForTaluka();
    return () => {
      cancelled = true;
    };
  }, [selectedTalukaId]);

  const buildFilterPayload = (filters) => {
    const payload = {};
    const nameMap = {
      state: 'stateName',
      district: 'districtName',
      taluka: 'talukaName',
      village: 'villageName',
      profession: 'professionName',
      castcategory: 'categoryName',
      cast: 'castName',
      education: 'educationName'
    };

    Object.keys(FILTER_LABELS).forEach((key) => {
      if (key === 'incomeRange' || key === 'minIncome' || key === 'maxIncome') return;

      const value = filters[key];
      if (value == null || value === '') return;

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) payload[key] = trimmed;
        return;
      }

      const name = getOptionName(value, nameMap[key]);
      if (name) payload[key] = name;
    });

    if (filters.incomeRange) {
      const { minIncome, maxIncome } = parseIncomeRange(filters.incomeRange);
      if (minIncome != null) payload.minIncome = minIncome;
      if (maxIncome != null) payload.maxIncome = maxIncome;
    }

    return payload;
  };

  const hasAnyFilter = (filters) => Object.keys(buildFilterPayload(filters)).length > 0;

  const appliedPayload = useMemo(() => buildFilterPayload(appliedFilters), [appliedFilters]);

  useEffect(() => {
    let cancelled = false;
    const loadReport = async () => {
      if (Object.keys(appliedPayload).length === 0) {
        setReportRows([]);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      try {
        const response = await fetchDynamicFilterReport(appliedPayload);
        if (cancelled) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        setReportRows(rows);
        setError(null);
      } catch (err) {
        console.error('Error loading report data:', err);
        if (!cancelled) {
          setError('Failed to load report data. Please try again later.');
          setReportRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [appliedPayload]);

  const chartData = useMemo(
    () =>
      reportRows.map((row, index) => ({
        id: index,
        name: buildGroupLabel(row.groupValues),
        shortName:
          Object.values(row.groupValues || {}).join(' / ') || `Group ${index + 1}`,
        value: Number(row.count) || 0,
        groupValues: row.groupValues || {}
      })),
    [reportRows]
  );

  const totalCount = useMemo(
    () => chartData.reduce((sum, row) => sum + row.value, 0),
    [chartData]
  );

  const appliedChips = useMemo(() => {
    const chips = [];
    const payload = appliedPayload;
    const incomeLabel = getIncomeRange(payload.minIncome, payload.maxIncome);

    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'minIncome' || key === 'maxIncome') return;
      chips.push({
        key,
        label: `${FILTER_LABELS[key] || key}: ${value}`
      });
    });

    if (incomeLabel) {
      chips.push({
        key: 'incomeRange',
        label: `Income Range: ${incomeLabel}`
      });
    }

    return chips;
  }, [appliedPayload]);

  const handleFilterChange = (field, value) => {
    setSelectedFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'state') {
        next.district = null;
        next.taluka = null;
        next.village = null;
      } else if (field === 'district') {
        next.taluka = null;
        next.village = null;
      } else if (field === 'taluka') {
        next.village = null;
      }
      return next;
    });
  };

  const handleOpenFilterDialog = () => {
    setSelectedFilters({ ...appliedFilters });
    setFilterDialogOpen(true);
  };

  const handleCloseFilterDialog = () => {
    setSelectedFilters({ ...appliedFilters });
    setFilterDialogOpen(false);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...selectedFilters });
    setFilterDialogOpen(false);
  };

  const handleClearFilters = () => {
    setSelectedFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
    setFilterDialogOpen(false);
  };

  const districtOptions = selectedStateId ? filteredDistricts : districts;
  const talukaOptions = selectedDistrictId ? filteredTalukas : talukas;

  return (
    <Box sx={{ flexGrow: 1, m: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1">
          Reports
        </Typography>
        <Stack direction="row" spacing={1}>
          {hasAnyFilter(appliedFilters) && (
            <Button variant="outlined" color="secondary" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={handleOpenFilterDialog}
            color="primary"
          >
            Filters
          </Button>
        </Stack>
      </Box>

      {appliedChips.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {appliedChips.map((chip) => (
            <Chip key={chip.key} label={chip.label} color="primary" variant="outlined" />
          ))}
        </Stack>
      )}

      <Dialog
        open={filterDialogOpen}
        onClose={handleCloseFilterDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle>
          Report Filters
          <IconButton
            aria-label="close"
            onClick={handleCloseFilterDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select one or more fields to filter. Values are sent as names to the reports API
            (for example gender: Male, state: Maharashtra).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={GENDER_OPTIONS}
                value={selectedFilters.gender}
                onChange={(_, newValue) => handleFilterChange('gender', newValue)}
                renderInput={(params) => <TextField {...params} label="Gender" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={states}
                getOptionLabel={(option) => getOptionName(option, 'stateName')}
                value={selectedFilters.state}
                onChange={(_, newValue) => handleFilterChange('state', newValue)}
                isOptionEqualToValue={(option, value) =>
                  String(getOptionId(option, 'stateId')) === String(getOptionId(value, 'stateId'))
                }
                renderInput={(params) => <TextField {...params} label="State" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={districtOptions}
                getOptionLabel={(option) => getOptionName(option, 'districtName')}
                value={selectedFilters.district}
                onChange={(_, newValue) => handleFilterChange('district', newValue)}
                disabled={!selectedStateId}
                isOptionEqualToValue={(option, value) =>
                  String(getOptionId(option, 'districtId')) === String(getOptionId(value, 'districtId'))
                }
                renderInput={(params) => <TextField {...params} label="District" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={talukaOptions}
                getOptionLabel={(option) => getOptionName(option, 'talukaName')}
                value={selectedFilters.taluka}
                onChange={(_, newValue) => handleFilterChange('taluka', newValue)}
                disabled={!selectedDistrictId}
                isOptionEqualToValue={(option, value) =>
                  String(getOptionId(option, 'talukaId')) === String(getOptionId(value, 'talukaId'))
                }
                renderInput={(params) => <TextField {...params} label="Taluka" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={filteredVillages}
                getOptionLabel={(option) => getOptionName(option, 'villageName')}
                value={selectedFilters.village}
                onChange={(_, newValue) => handleFilterChange('village', newValue)}
                disabled={!selectedTalukaId}
                isOptionEqualToValue={(option, value) =>
                  String(getOptionId(option, 'villageId')) === String(getOptionId(value, 'villageId'))
                }
                renderInput={(params) => <TextField {...params} label="Village" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={professions}
                getOptionLabel={(option) => getOptionName(option, 'professionName')}
                value={selectedFilters.profession}
                onChange={(_, newValue) => handleFilterChange('profession', newValue)}
                isOptionEqualToValue={(option, value) =>
                  String(getOptionId(option, 'professionId')) === String(getOptionId(value, 'professionId'))
                }
                renderInput={(params) => <TextField {...params} label="Profession" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={castCategories}
                getOptionLabel={(option) => getOptionName(option, 'categoryName')}
                value={selectedFilters.castcategory}
                onChange={(_, newValue) => handleFilterChange('castcategory', newValue)}
                isOptionEqualToValue={(option, value) =>
                  String(getOptionId(option, 'categoryId')) === String(getOptionId(value, 'categoryId'))
                }
                renderInput={(params) => <TextField {...params} label="Caste Category" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={casts}
                getOptionLabel={(option) => getOptionName(option, 'castName')}
                value={selectedFilters.cast}
                onChange={(_, newValue) => handleFilterChange('cast', newValue)}
                isOptionEqualToValue={(option, value) =>
                  String(getOptionId(option, 'castId')) === String(getOptionId(value, 'castId'))
                }
                renderInput={(params) => <TextField {...params} label="Caste" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={educationOptions}
                getOptionLabel={(option) => getOptionName(option, 'educationName')}
                value={selectedFilters.education}
                onChange={(_, newValue) => handleFilterChange('education', newValue)}
                isOptionEqualToValue={(option, value) =>
                  String(getOptionId(option, 'educationId')) === String(getOptionId(value, 'educationId'))
                }
                renderInput={(params) => <TextField {...params} label="Education" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={INCOME_RANGE_OPTIONS}
                value={selectedFilters.incomeRange}
                onChange={(_, newValue) => handleFilterChange('incomeRange', newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Income Range" />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={DISABILITY_OPTIONS}
                value={selectedFilters.disability}
                onChange={(_, newValue) => handleFilterChange('disability', newValue)}
                renderInput={(params) => <TextField {...params} label="Disability" />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearFilters} color="secondary">
            Clear
          </Button>
          <Button onClick={handleCloseFilterDialog}>Cancel</Button>
          <Button onClick={handleApplyFilters} variant="contained" color="primary">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      ) : chartData.length > 0 ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="h6">Filter Result Count</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total matching records: {totalCount.toLocaleString()}
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={chartType}
                    onChange={(_, value) => value && setChartType(value)}
                  >
                    <ToggleButton value="bar">Bar</ToggleButton>
                    <ToggleButton value="pie">Pie</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <ResponsiveContainer width="100%" height={420}>
                  {chartType === 'pie' ? (
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="shortName"
                        cx="50%"
                        cy="50%"
                        outerRadius={140}
                        label={({ shortName, value }) => `${shortName}: ${value}`}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={entry.id} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [Number(value).toLocaleString(), 'Count']}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
                      />
                      <Legend />
                    </PieChart>
                  ) : (
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="shortName"
                        angle={-25}
                        textAnchor="end"
                        height={90}
                        interval={0}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        formatter={(value) => [Number(value).toLocaleString(), 'Count']}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
                      />
                      <Legend />
                      <Bar dataKey="value" name="Count" fill="#1976d2" radius={[6, 6, 0, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell key={entry.id} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {hasAnyFilter(appliedFilters)
              ? 'No report data for the selected filters'
              : 'No reports data available'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasAnyFilter(appliedFilters)
              ? 'Try adjusting your filter values'
              : 'Click Filters and select values to generate a report'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default Reports;
